/**
 * GraphQL Admin Query Console Hooks
 *
 * Hooks for admin SQL query console operations:
 * - useAdminSchemaTablesGraphQL: Fetch database schema tables
 * - useAdminExecuteQueryGraphQL: Execute SQL queries (SELECT only)
 *
 * Feature: 055-db-console
 */

import { useQuery, useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Query for schema tables
const ADMIN_SCHEMA_TABLES_QUERY = gql`
  query AdminSchemaTables {
    adminSchemaTables {
      name
      columnCount
      columnNames
    }
  }
`;

// GraphQL Query for table columns
const ADMIN_SCHEMA_TABLE_COLUMNS_QUERY = gql`
  query AdminSchemaTableColumns($tableName: String!) {
    adminSchemaTableColumns(tableName: $tableName) {
      name
      dataType
      isNullable
      defaultValue
      isPrimaryKey
    }
  }
`;

// GraphQL Mutation for executing SQL queries
const ADMIN_EXECUTE_QUERY_MUTATION = gql`
  mutation AdminExecuteQuery($sql: String!) {
    adminExecuteQuery(sql: $sql) {
      rows
      rowCount
      executionTime
      truncated
      message
    }
  }
`;

// Types
export interface SchemaTable {
  name: string;
  columnCount: number;
  columnNames: string[];
}

export interface SchemaColumn {
  name: string;
  dataType: string;
  isNullable: boolean;
  defaultValue: string | null;
  isPrimaryKey: boolean;
}

export interface QueryResult {
  rows: Record<string, unknown>[];
  rowCount: number;
  executionTime: number;
  truncated: boolean;
  message?: string;
}

/**
 * Hook for fetching database schema tables
 *
 * Used by SchemaExplorer component to display table list
 */
export function useAdminSchemaTablesGraphQL() {
  const { data, loading, error, refetch } = useQuery<{
    adminSchemaTables: SchemaTable[];
  }>(ADMIN_SCHEMA_TABLES_QUERY, {
    onError: (err) => {
      logger.error('AdminQuery', 'Failed to fetch schema tables', err);
    },
  });

  return {
    tables: data?.adminSchemaTables || [],
    loading,
    error: error?.message || null,
    refetch,
  };
}

/**
 * Hook for fetching table columns
 *
 * Used by SchemaExplorer tree view to display column details
 */
export function useAdminSchemaTableColumnsGraphQL(tableName: string | null) {
  const { data, loading, error } = useQuery<{
    adminSchemaTableColumns: SchemaColumn[];
  }>(ADMIN_SCHEMA_TABLE_COLUMNS_QUERY, {
    variables: { tableName: tableName || '' },
    skip: !tableName,
    onError: (err) => {
      logger.error('AdminQuery', 'Failed to fetch table columns', { tableName, error: err });
    },
  });

  return {
    columns: data?.adminSchemaTableColumns || [],
    loading,
    error: error?.message || null,
  };
}

/**
 * Hook for executing SQL queries
 *
 * Used by AdminQueryConsolePage to run SELECT queries
 */
export function useAdminExecuteQueryGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { adminExecuteQuery: QueryResult },
    { sql: string }
  >(ADMIN_EXECUTE_QUERY_MUTATION, {
    onError: (err) => {
      logger.error('AdminQuery', 'Query execution failed', err);
    },
  });

  const executeQuery = async (sql: string): Promise<QueryResult | null> => {
    logger.info('AdminQuery', 'Executing query via GraphQL', {
      query: sql.substring(0, 100),
    });

    try {
      const result = await mutate({ variables: { sql } });

      if (result.data?.adminExecuteQuery) {
        logger.info('AdminQuery', 'Query executed successfully', {
          rowCount: result.data.adminExecuteQuery.rowCount,
          executionTime: result.data.adminExecuteQuery.executionTime,
        });
      }

      return result.data?.adminExecuteQuery || null;
    } catch (err) {
      // Error is already logged by onError callback
      throw err;
    }
  };

  return {
    executeQuery,
    loading,
    error: error?.message || null,
  };
}
